import { useState, useEffect, useCallback } from 'react';
import { Download, Upload, Server, AlertTriangle, CheckCircle, XCircle, RefreshCw, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BackupPage = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Restore state
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [restoreInProgress, setRestoreInProgress] = useState(false);
  
  // Auto-Backup state
  const [autoBackups, setAutoBackups] = useState([]);
  const [autoBackupsLoading, setAutoBackupsLoading] = useState(false);

  const getToken = () => {
    const savedUser = localStorage.getItem('gb-sylt-user');
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        return parsed.token;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  // Load auto-backups on mount
  const loadAutoBackups = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    
    setAutoBackupsLoading(true);
    try {
      const response = await axios.get(`${API}/backup/auto/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoBackups(response.data.backups || []);
    } catch (error) {
      console.error('Fehler beim Laden der Auto-Backups:', error);
      setAutoBackups([]);
    } finally {
      setAutoBackupsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAutoBackups();
  }, [loadAutoBackups]);

  const handleCreateBackup = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = getToken();
      if (!token) {
        setMessage({ type: 'error', text: 'Keine Authentifizierung. Bitte erneut anmelden.' });
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API}/backup/create`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 60000
      });

      if (response.status < 200 || response.status >= 300) {
        throw new Error('Backup fehlgeschlagen (HTTP ' + response.status + ')');
      }

      const contentDisposition = response.headers['content-disposition'];
      let filename = `gb-wissen-backup_${new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')}.json`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => {
        try {
          window.URL.revokeObjectURL(url);
        } catch (e) {
          console.warn('URL cleanup warning:', e);
        }
      }, 1000);

      setMessage({ type: 'success', text: `Backup erfolgreich heruntergeladen: ${filename}` });
      setLoading(false);
      
    } catch (error) {
      console.error('Backup error:', error);
      let errorMsg = 'Fehler beim Erstellen des Backups';
      
      if (error.response?.data) {
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const errorData = JSON.parse(text);
            errorMsg = errorData.detail || errorMsg;
          } catch (e) {
            // Keep default error message
          }
        } else {
          errorMsg = error.response.data.detail || errorMsg;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      setMessage({ type: 'error', text: errorMsg });
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setValidationResult(null);
    setConfirmRestore(false);
    setMessage({ type: '', text: '' });

    setLoading(true);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/backup/validate`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setValidationResult(response.data);
    } catch (error) {
      setValidationResult({
        valid: false,
        error: error.response?.data?.detail || 'Fehler bei der Validierung',
        can_restore: false
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile || !confirmRestore || !validationResult?.can_restore) return;

    setRestoreInProgress(true);
    setMessage({ type: '', text: '' });

    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('confirm', 'CONFIRMED');

      const response = await axios.post(`${API}/backup/restore`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessage({ 
        type: 'success', 
        text: `Backup erfolgreich wiederhergestellt! ${response.data.restored.users} Benutzer, ${response.data.restored.knowledge_entries} Wissenseinträge.` 
      });
      
      resetRestoreState();

    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Fehler bei der Wiederherstellung';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setRestoreInProgress(false);
    }
  };

  const resetRestoreState = () => {
    setSelectedFile(null);
    setValidationResult(null);
    setConfirmRestore(false);
    const fileInput = document.getElementById('backup-file-input');
    if (fileInput) fileInput.value = '';
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
        
        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-100 border border-green-200 text-green-800 dark:bg-green-900/30 dark:border-green-800 dark:text-green-200' 
              : 'bg-red-100 border border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-800 dark:text-red-200'
          }`}>
            {message.type === 'success' ? <CheckCircle className="h-5 w-5 flex-shrink-0" /> : <XCircle className="h-5 w-5 flex-shrink-0" />}
            <span className="text-sm">{message.text}</span>
          </div>
        )}

        {/* 1. Backup herunterladen (lokal) */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-4 md:p-5">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Download className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  Backup herunterladen (lokal)
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Erstellt eine vollständige JSON-Datei und lädt sie auf Ihren Computer herunter. 
                  Die Datei landet in Ihrem Download-Ordner.
                </p>
                <button
                  onClick={handleCreateBackup}
                  disabled={loading}
                  data-testid="download-backup-btn"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition-colors"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Wird erstellt...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      JSON herunterladen
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 2. Backup wiederherstellen */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-4 md:p-5">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 bg-orange-100 dark:bg-orange-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Upload className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  Backup wiederherstellen
                </h2>
                
                {/* Warning Badge */}
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md text-xs font-medium mb-4">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Überschreibt vorhandene Daten!
                </div>

                {/* File Input */}
                <div className="space-y-4">
                  <div>
                    <input
                      id="backup-file-input"
                      type="file"
                      accept=".json"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <label
                      htmlFor="backup-file-input"
                      className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer font-medium text-sm transition-colors"
                    >
                      <Upload className="h-4 w-4" />
                      Backup-Datei auswählen
                    </label>
                    {selectedFile && (
                      <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">
                        {selectedFile.name}
                      </span>
                    )}
                  </div>

                  {/* Validation Result */}
                  {validationResult && (
                    <div className={`p-4 rounded-lg border ${
                      validationResult.valid 
                        ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' 
                        : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
                    }`}>
                      {validationResult.valid ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-medium text-sm">
                            <CheckCircle className="h-4 w-4" />
                            Backup gültig
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-300 grid grid-cols-2 gap-x-4 gap-y-1">
                            <span>Erstellt: {formatDate(validationResult.created_at)}</span>
                            <span>Von: {validationResult.created_by}</span>
                            <span>Benutzer: {validationResult.statistics?.users_count || 0}</span>
                            <span>Wissen: {validationResult.statistics?.knowledge_count || 0}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-red-700 dark:text-red-400 text-sm">
                          <XCircle className="h-4 w-4" />
                          {validationResult.error}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Restore Confirmation */}
                  {validationResult?.can_restore && (
                    <div className="space-y-3">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={confirmRestore}
                          onChange={(e) => setConfirmRestore(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Ich verstehe, dass alle aktuellen Daten überschrieben werden.
                        </span>
                      </label>

                      <div className="flex gap-3">
                        <button
                          onClick={resetRestoreState}
                          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium transition-colors"
                        >
                          Abbrechen
                        </button>
                        <button
                          onClick={handleRestore}
                          disabled={!confirmRestore || restoreInProgress}
                          data-testid="restore-backup-btn"
                          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2 transition-colors"
                        >
                          {restoreInProgress ? (
                            <>
                              <RefreshCw className="h-4 w-4 animate-spin" />
                              Wird wiederhergestellt...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4" />
                              Wiederherstellen
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 3. Automatische Server-Backups */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-4 md:p-5">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 bg-purple-100 dark:bg-purple-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Server className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 mb-1">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Automatische Server-Backups
                  </h2>
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-md text-xs font-medium flex-shrink-0">
                    <Clock className="h-3.5 w-3.5" />
                    Täglich 03:00 Uhr
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Backups werden automatisch auf dem Server erstellt und rotiert. 
                  Die letzten 14 Backups werden aufbewahrt.
                </p>

                {/* Backups Table */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  {autoBackupsLoading ? (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                      <span className="text-sm">Lade Backups...</span>
                    </div>
                  ) : autoBackups.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                      <Server className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Noch keine automatischen Backups vorhanden.</p>
                      <p className="text-xs mt-1">Das erste Backup wird um 03:00 Uhr erstellt.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-900/50">
                          <tr>
                            <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Dateiname</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Typ</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Erstellt am</th>
                            <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Größe</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {autoBackups.map((backup, index) => (
                            <tr key={backup.filename} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                              <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-xs">{backup.filename}</span>
                                  {index === 0 && (
                                    <span className="inline-flex px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-medium">
                                      Neueste
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                  backup.type === 'Manuell' 
                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                }`}>
                                  {backup.type || 'Automatisch'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                                {formatDate(backup.created_at)}
                              </td>
                              <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-right">
                                {backup.size_readable || formatSize(backup.size_bytes)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Refresh Button */}
                <button
                  onClick={loadAutoBackups}
                  disabled={autoBackupsLoading}
                  className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <RefreshCw className={`h-4 w-4 ${autoBackupsLoading ? 'animate-spin' : ''}`} />
                  Aktualisieren
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};
